/**
 * Google Play Billing — purchase the practitioner monthly subscription.
 *
 * Mirror of `appleIAP.js` but for Android. Uses the same
 * `cordova-plugin-purchase` plugin (which handles both stores).
 *
 * Plugin install (in iOS shell too — single plugin):
 *   yarn add cordova-plugin-purchase
 *   npx cap sync android
 *
 * On every other platform (web, iOS, Android web preview), exported
 * functions return `{ ok: false, reason }` so callers can fall back.
 */
import { api } from "./api";

let _store = null;
let _productInfo = null;
let _readyPromise = null;

async function _getServerConfig() {
  if (_productInfo) return _productInfo;
  const { data } = await api.get("/iap/google/config");
  _productInfo = data;
  return data;
}

export function initGooglePlayBilling() {
  if (_readyPromise) return _readyPromise;
  _readyPromise = (async () => {
    const cfg = await _getServerConfig();
    if (!cfg.configured) {
      throw new Error("Google Play IAP is not configured on the server yet");
    }
    const CdvPurchase = window.CdvPurchase;
    if (!CdvPurchase) {
      throw new Error(
        "cordova-plugin-purchase not installed — run `yarn add cordova-plugin-purchase && npx cap sync android` and rebuild the Android shell."
      );
    }
    const store = CdvPurchase.store;
    _store = store;
    const productId = cfg.productIds.practitionerSubscriptionMonthly;
    store.register([{
      id: productId,
      type: CdvPurchase.ProductType.PAID_SUBSCRIPTION,
      platform: CdvPurchase.Platform.GOOGLE_PLAY,
    }]);

    // Backend-verify every approved transaction. Once the backend
    // confirms, finish() the transaction so Google Play stops nagging.
    store.when().approved(async (transaction) => {
      try {
        const purchaseToken = transaction.nativePurchase?.purchaseToken
          || transaction.transactionId;
        if (!purchaseToken) {
          console.warn("[google-iap] approved tx with no purchaseToken", transaction);
          transaction.finish();
          return;
        }
        await api.post("/iap/google/verify", {
          purchaseToken,
          productId: transaction.products?.[0]?.id || productId,
        });
        transaction.finish();
      } catch (err) {
        console.error("[google-iap] verify failed", err);
      }
    });

    await store.initialize([CdvPurchase.Platform.GOOGLE_PLAY]);
    await store.update();
    return { store, productId };
  })();
  return _readyPromise;
}

export async function purchasePractitionerSubscriptionAndroid() {
  try {
    const { store, productId } = await initGooglePlayBilling();
    const product = store.get(productId);
    if (!product) return { ok: false, reason: "product-not-loaded" };
    const offer = product.getOffer();
    if (!offer) return { ok: false, reason: "no-purchase-offer-available" };
    await store.order(offer);
    return { ok: true };
  } catch (err) {
    return { ok: false, reason: err?.message || "iap-failed" };
  }
}

export async function restorePurchasesAndroid() {
  try {
    const { store } = await initGooglePlayBilling();
    await store.restorePurchases();
    return { ok: true };
  } catch (err) {
    return { ok: false, reason: err?.message || "restore-failed" };
  }
}
